'use client'
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import AgentPicture from '../../../../../public/images/AI-onl.png';
import Image from "next/image";
import GeneralAgent from "../GeneralAgent/GeneralAgent";
import { usePathname, useRouter } from "next/navigation";
import sectorService from "@/templates/Starglobal3D/utils/services/sector";
import MapOverlay from "../Footer/MapOverlay";
import { useLanguage } from "@/contexts/I18nProvider";
import PlatformProjectViewerModal, { PlatformProject } from "../ProjectViewerModal/platform-project-viewer-modal";
import { usePlatformMessageBridge } from "../../hooks/usePlatformMessageBridge";
import { buildPlatformToolsRegistry, getPlatformSystemInstruction } from "../../lib/platform-tools-registry";

import { isLocale } from '@/utils/constant/locales';

const SECTOR_URL_MAPPING: Record<string, string> = {
    'farm': 'agriculture',
};

const SECTOR_ALIAS_MAPPING: Record<string, string> = {
    'congnghiep': 'zone',
    'khucongnghiep': 'zone',
    'industrial': 'zone',
    'industrialzone': 'zone',
    'industrialpark': 'zone',
    'factory': 'factory',
    'nhamay': 'factory',
    'farm': 'farm',
    'trangtrai': 'farm',
    'nongnghiep': 'farm',
    'agriculture': 'farm',
    'tourism': 'tourism',
    'dulich': 'tourism',
    'heritage': 'heritage',
    'disan': 'heritage',
    'museum': 'museum',
    'baotang': 'museum',
    'city': 'city',
    'dothi': 'city',
    'dothithongminh': 'city',
    'smartcity': 'city',
    'facility': 'facility',
    'hatang': 'facility',
    'cosohatang': 'facility',
    'school': 'school',
    'truonghoc': 'school',
    'giaoduc': 'school',
    'oilandgas': 'oil-and-gas',
    'daukhi': 'oil-and-gas',
    'realestate': 'real-estate',
    'estate': 'real-estate',
    'batdongsan': 'real-estate',
    'office': 'office',
    'vanphong': 'office',
    'exhibition': 'exhibition',
    'trienlam': 'exhibition',
    'showroom': 'showroom',
    'trungbay': 'showroom',
};

const findSectorSlug = async (inputSector: string): Promise<string> => {
    if (!inputSector) return '';
    
    // Chuẩn hóa chuỗi đầu vào (bỏ dấu tiếng Việt, ký tự đặc biệt) để đối chiếu dễ dàng
    const cleanInput = inputSector.toLowerCase().trim()
        .normalize('NFD').replace(/[\u0300-\u036F]/g, '')
        .replace(/đ/g, 'd')
        .replace(/[^a-z0-9]/g, '');

    // 1. Kiểm tra khớp hoàn toàn trong từ điển alias
    if (SECTOR_ALIAS_MAPPING[cleanInput]) {
        const mappedId = SECTOR_ALIAS_MAPPING[cleanInput];
        return SECTOR_URL_MAPPING[mappedId] || mappedId;
    }

    // 2. Kiểm tra khớp một phần (substring) trong từ điển alias
    const matchedKey = Object.keys(SECTOR_ALIAS_MAPPING).find(key => cleanInput.includes(key));
    if (matchedKey) {
        const mappedId = SECTOR_ALIAS_MAPPING[matchedKey];
        return SECTOR_URL_MAPPING[mappedId] || mappedId;
    }

    try {
        const res = await sectorService.getAllNestedSectors();
        if (res?.data && Array.isArray(res.data)) {
            const allSectors = res.data.flatMap((group: any) => group.fields || []);
            
            // Tìm kiếm sector khớp với id_field, display_vn hoặc display_en
            const matchedSector = allSectors.find((s: any) => {
                const idField = (s.id_field || '').toLowerCase();
                const displayVn = (s.display_vn || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036F]/g, '').replace(/đ/g, 'd');
                const displayEn = (s.display_en || '').toLowerCase();

                return idField.replace(/[^a-z0-9]/g, '') === cleanInput ||
                       displayVn.replace(/[^a-z0-9]/g, '') === cleanInput ||
                       displayEn.replace(/[^a-z0-9]/g, '') === cleanInput;
            });

            if (matchedSector) {
                const idField = matchedSector.id_field;
                return SECTOR_URL_MAPPING[idField] || idField;
            }
        }
    } catch (e) {
        console.error("Error fetching sectors in MainFooter:", e);
    }

    // Fallback: Nếu không tìm thấy trong DB, tự động chuẩn hóa input làm slug
    const fallbackName = inputSector.toLowerCase().trim()
        .normalize('NFD').replace(/[\u0300-\u036F]/g, '')
        .replace(/đ/g, 'd')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
        
    return SECTOR_URL_MAPPING[fallbackName] || fallbackName;
};

export default function MainFooter() {
    const { locale } = useLanguage();
    const getLocalizedPath = (path: string) => {
        if (!locale || locale === 'vn') return path;
        return `/${locale}${path}`;
    };

    const [isOpen, setIsOpen] = useState(false);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [openedFromPath, setOpenedFromPath] = useState<string | null>(null);
    const pathname = usePathname();
    
    const segments = pathname.split('/').filter(Boolean);
    const isHome = pathname === '/' || (segments.length === 1 && isLocale(segments[0]));

    // also listen to home's internal state via custom event if needed
    const [isHomeInterfaceOpen, setIsHomeInterfaceOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isSectorListOpen, setIsSectorListOpen] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isMapFilterOpen, setIsMapFilterOpen] = useState(false);
    const router = useRouter();

    /* ═══════════════════════════════════════════════════════════
     * PROJECT VIEWER MODAL STATE (for agent-triggered opens)
     * ═══════════════════════════════════════════════════════════ */
    const [activeProject, setActiveProject] = useState<PlatformProject | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { sendCommand } = usePlatformMessageBridge();

    // Keep activeProject in a ref for stable access in tool handlers
    const activeProjectRef = useRef(activeProject);
    activeProjectRef.current = activeProject;

    const openProject = useCallback((project: PlatformProject) => {
        setActiveProject(project);
    }, []);

    const closeProject = useCallback(() => {
        setActiveProject(null);
    }, []);

    // Lock body scroll when project viewer is open
    useEffect(() => {
        if (!activeProject) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previousOverflow; };
    }, [activeProject]);

    // Keep iframeRef stable for tool handlers
    const iframeRefStable = useRef(iframeRef);
    iframeRefStable.current = iframeRef;

    /* ═══════════════════════════════════════════════════════════
     * BUILD PLATFORM TOOLS FOR AGENT
     * ═══════════════════════════════════════════════════════════ */
    const platformToolContext = useMemo(() => ({
        openProject,
        closeProject,
        getActiveProject: () => activeProjectRef.current,
        sendIframeCommand: async (action: string, params: Record<string, any>) => {
            const iframe = iframeRefStable.current?.current ?? null;
            return sendCommand(iframe, action, params, 12000);
        },
    }), [openProject, closeProject, sendCommand]);

    const platformTools = useMemo(
        () => buildPlatformToolsRegistry(platformToolContext),
        [platformToolContext],
    );

    const [sectorName, setSectorName] = useState<string | undefined>(undefined);

    const sectorSlug = useMemo(() => {
        let cleanPath = pathname?.replace(/\/$/, '') || '';
        const cleanPathSegments = cleanPath.split('/').filter(Boolean);
        if (cleanPathSegments.length > 0 && isLocale(cleanPathSegments[0])) {
            cleanPath = '/' + cleanPathSegments.slice(1).join('/');
            cleanPath = cleanPath.replace(/\/$/, '');
        }
        if (cleanPath === '') cleanPath = '/';
        const isSector = cleanPath.startsWith('/smart-') && cleanPath.endsWith('-360');
        return isSector ? cleanPath.replace('/smart-', '').replace('-360', '') : null;
    }, [pathname]);

    useEffect(() => {
        if (!sectorSlug) {
            setSectorName(undefined);
            return;
        }

        const fetchSectorName = async () => {
            try {
                const res = await sectorService.getAllNestedSectors();
                if (res?.data && Array.isArray(res.data)) {
                    const allSectors = res.data.flatMap((group: any) => group.fields || []);
                    
                    // Match the slug with id_field or URL mapping
                    const matchedSector = allSectors.find((s: any) => {
                        const idField = (s.id_field || '').toLowerCase();
                        // Also check reverse mapping in case the slug is different
                        const expectedSlug = Object.entries(SECTOR_URL_MAPPING).find(([k, v]) => k === idField)?.[1] || idField;
                        return expectedSlug === sectorSlug || idField === sectorSlug;
                    });

                    if (matchedSector) {
                        setSectorName(matchedSector.display_vn || matchedSector.display_en || sectorSlug);
                    } else {
                        // Fallback if not found in DB
                        setSectorName(undefined);
                    }
                }
            } catch (e) {
                console.error("Error fetching sectors for agent greeting:", e);
                setSectorName(undefined);
            }
        };

        fetchSectorName();
    }, [sectorSlug]);

    const platformSystemInstruction = useMemo(
        () => getPlatformSystemInstruction('Libertie', sectorName),
        [sectorName],
    );

    /* ═══════════════════════════════════════════════════════════
     * EXISTING MAP / SECTOR EVENT HANDLERS (unchanged)
     * ═══════════════════════════════════════════════════════════ */

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const isMapUrl = window.location.pathname.includes('/map3d');
            if (isMapUrl) {
                setIsMapOpen(true);
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('global_map_state', { detail: { isOpen: true } }));
                }, 100);
            }
        }
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            const isMapUrl = window.location.pathname.includes('/map3d');
            setIsMapOpen(isMapUrl);
            window.dispatchEvent(new CustomEvent('global_map_state', { detail: { isOpen: isMapUrl } }));
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    useEffect(() => {
        const handleSearchState = (e: any) => {
            setIsSearchOpen(!!e.detail?.isOpen);
        };
        const handleSectorState = (e: any) => {
            setIsSectorListOpen(!!e.detail?.isOpen);
        };
        const handleContactState = (e: any) => {
            setIsContactOpen(!!e.detail?.isOpen);
        };
        const handleMapFilterState = (e: any) => {
            setIsMapFilterOpen(!!e.detail?.isOpen);
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('global_search_state', handleSearchState);
            window.addEventListener('global_sector_state', handleSectorState);
            window.addEventListener('global_contact_state', handleContactState);
            window.addEventListener('global_map_filter_state', handleMapFilterState);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('global_search_state', handleSearchState);
                window.removeEventListener('global_sector_state', handleSectorState);
                window.removeEventListener('global_contact_state', handleContactState);
                window.removeEventListener('global_map_filter_state', handleMapFilterState);
            }
        };
    }, []);

    useEffect(() => {
        const handleOpenSectorList = () => setIsHomeInterfaceOpen(true);
        const handleCloseSectorList = () => setIsHomeInterfaceOpen(false);

        const handleOpenSectorProjectList = async (e: any) => {
            if (e.detail?.sector) {
                const slug = await findSectorSlug(e.detail.sector);
                router.push(getLocalizedPath(`/smart-${slug}-360`));
            }
        };

        const handleCloseSectorProjectList = () => {
            router.push(getLocalizedPath('/'));
        };

        window.addEventListener('open_sector_list', handleOpenSectorList);
        window.addEventListener('close_sector_list', handleCloseSectorList);
        window.addEventListener('open_sector_project_list', handleOpenSectorProjectList);
        window.addEventListener('close_sector_project_list', handleCloseSectorProjectList);

        return () => {
            window.removeEventListener('open_sector_list', handleOpenSectorList);
            window.removeEventListener('close_sector_list', handleCloseSectorList);
            window.removeEventListener('open_sector_project_list', handleOpenSectorProjectList);
            window.removeEventListener('close_sector_project_list', handleCloseSectorProjectList);
        }
    }, [router]);

    useEffect(() => {
        const handleOpenMap = () => {
            setIsMapOpen(true);
            window.dispatchEvent(new CustomEvent('global_map_state', { detail: { isOpen: true } }));
            
            const currentPath = window.location.pathname;
            const segments = currentPath.split('/').filter(Boolean);
            const isPathHome = currentPath === '/' || (segments.length === 1 && isLocale(segments[0]));
            
            const mapPath = getLocalizedPath('/map3d');
            if (!isPathHome && !currentPath.includes('/map3d')) {
                setOpenedFromPath(currentPath);
                router.push(mapPath);
            } else {
                if (!currentPath.includes('/map3d')) {
                    window.history.pushState(null, '', mapPath);
                }
            }
        };

        const handleFilterMap = () => {
            setIsMapOpen(true);
            window.dispatchEvent(new CustomEvent('global_map_state', { detail: { isOpen: true } }));
            
            const currentPath = window.location.pathname;
            const segments = currentPath.split('/').filter(Boolean);
            const isPathHome = currentPath === '/' || (segments.length === 1 && isLocale(segments[0]));
            
            const mapPath = getLocalizedPath('/map3d');
            if (!isPathHome && !currentPath.includes('/map3d')) {
                setOpenedFromPath(currentPath);
                router.push(mapPath);
            } else {
                if (!currentPath.includes('/map3d')) {
                    window.history.pushState(null, '', mapPath);
                }
            }
        };

        window.addEventListener('open_map_directions', handleOpenMap);
        window.addEventListener('filter_map_projects', handleFilterMap);

        return () => {
            window.removeEventListener('open_map_directions', handleOpenMap);
            window.removeEventListener('filter_map_projects', handleFilterMap);
        };
    }, [isHome, router, locale]);

    const handleCloseMap = useCallback(() => {
        setIsMapOpen(false);
        window.dispatchEvent(new CustomEvent('global_map_state', { detail: { isOpen: false } }));
        
        const isOriginalPathHome = !openedFromPath || openedFromPath === '/' || (() => {
            const segs = openedFromPath.split('/').filter(Boolean);
            return segs.length === 1 && isLocale(segs[0]);
        })();
        
        if (openedFromPath && !isOriginalPathHome) {
            router.push(openedFromPath);
        } else {
            if (window.location.pathname.includes('/map3d')) {
                const url = new URL(window.location.href);
                url.pathname = url.pathname.replace(/\/map3d\/?$/, '');
                url.searchParams.delete('country');
                url.searchParams.delete('provinces');
                url.searchParams.delete('fields');
                url.searchParams.delete('project');
                url.searchParams.delete('projects');
                window.history.replaceState(null, '', url.pathname + url.search);
            }
        }
        setOpenedFromPath(null);
    }, [openedFromPath, router]);

    useEffect(() => {
        const handleCloseMapEvent = () => {
            handleCloseMap();
        };
        window.addEventListener('close_map', handleCloseMapEvent);
        return () => {
            window.removeEventListener('close_map', handleCloseMapEvent);
        };
    }, [handleCloseMap]);

    const cleanPath = pathname?.replace(/\/$/, '') || '';
    
    // Chuẩn hóa cleanPath để xóa prefix ngôn ngữ trước khi so sánh
    let normalizedCleanPath = cleanPath;
    const cleanPathSegments = cleanPath.split('/').filter(Boolean);
    if (cleanPathSegments.length > 0 && isLocale(cleanPathSegments[0])) {
        normalizedCleanPath = '/' + cleanPathSegments.slice(1).join('/');
        normalizedCleanPath = normalizedCleanPath.replace(/\/$/, '');
    }
    if (normalizedCleanPath === '') normalizedCleanPath = '/';

    const isHomepage = normalizedCleanPath === '/';
    const isMapPage = normalizedCleanPath === '/map3d' || isMapOpen;
    const isSectorPage = normalizedCleanPath.startsWith('/smart-') && normalizedCleanPath.endsWith('-360');

    const shouldShowAgent = isHomepage || isMapPage || isSectorPage;
    const shouldHide = !shouldShowAgent;
    const hasActiveOverlay = isSearchOpen || isSectorListOpen || isContactOpen;

    const hubOpeningMessage = sectorName
        ? `Bạn vừa được khởi động trên trang lĩnh vực ${sectorName}. Hãy chào người dùng bằng tiếng Việt chính xác như sau: "Xin chào, tôi là Libertie, chuyên gia số AI để đồng hành và hướng dẫn Anh Chị khám phá các giải pháp Bản sao số cho lĩnh vực ${sectorName}."`
        : `Bạn vừa được khởi động trên trang chủ (platform). Hãy chào người dùng bằng tiếng Việt tự nhiên, giới thiệu bạn là Libertie - Hướng dẫn viên ảo 3D thông minh của StarGlobal 3D, và mời họ khám phá các lĩnh vực công nghệ số 3D/360.`;

    return (
        <>
            {/* Agent + Thumbnail — always on homepage/map, also visible when project modal is open */}
            <div className={`fixed inset-0 pointer-events-none ${
                activeProject
                    ? 'z-[200]'
                    : isMapFilterOpen 
                        ? 'z-[115]' 
                        : (hasActiveOverlay ? 'z-[200]' : 'z-[120]')
            } ${(shouldHide && !activeProject) ? '!hidden' : ''}`}>
                <div className={isMapOpen 
                    ? "fixed bottom-[190px] left-4 z-[120] flex flex-col items-start pointer-events-auto" 
                    : "fixed top-[84px] right-4 z-[120] flex flex-col items-end pointer-events-auto"
                }>
                    {!isOpen &&
                        <div
                            onClick={() => setIsOpen(true)}
                            className="w-[80px] h-[80px] cursor-pointer hover:scale-105 transition-transform duration-300 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
                        >
                            <Image src={AgentPicture} alt="AI Assistant" width={80} height={80} className="w-[80px] h-[80px]" />
                        </div>
                    }
                </div>

                {/* General Agent Corner Component — now with hub-mode tools */}
                <GeneralAgent
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    isMapMode={isMapOpen}
                    isHubMode={true}
                    hubTools={platformTools}
                    hubSystemInstruction={platformSystemInstruction}
                    hubOpeningMessage={hubOpeningMessage}
                />
            </div>

            {/* Project Viewer Modal — opened by agent's open_project_in_viewer tool */}
            <PlatformProjectViewerModal
                key={activeProject?.id || 'closed'}
                project={activeProject}
                onClose={closeProject}
                iframeRef={iframeRef}
            />

            <MapOverlay isOpen={isMapOpen} onClose={handleCloseMap} />
        </>
    );
}
